import { css } from '@emotion/react';
import { observer } from 'mobx-react-lite';
import { Resizable } from 're-resizable';
import { ReactElement, ReactNode } from 'react';
import { useMemo } from 'react';

import { useSDK } from '../../app';
import { styled } from '../../styled';
import type { Theme } from '../../theme/interface/detailed';
import { useTheme } from '../../theme/useTheme';
import { isMobile } from '../../utils/isMobile';
import { useResize } from '../../utils/useResize';
import { CloseChatDesktopButton } from '../closeChatDesktopButton';
import { ChatMobileContainer } from './chatMobileContainer';
import { DraggableArea } from './draggableArea';

type Interface = { children: ReactNode; className: string };

const Component = ({ children, className }: Interface): ReactElement => {
	const theme = useTheme();
	const rootStore = useSDK();
	const { uiStateStore, welcomeDataStore } = rootStore;
	const themeProps = theme.allSettings.containers.chatContainer;

	const currentWidth = uiStateStore.chatSize?.width || themeProps.width + '';
	const currentHeight = uiStateStore.chatSize?.height || themeProps.height + '';

	const onResize = useResize(uiStateStore.setChatSizes, uiStateStore.setChatPos, uiStateStore.chatPos);
	const size = useMemo(
		() => ({
			height: currentHeight,
			width: currentWidth,
		}),
		[currentHeight, currentWidth]
	);

	/**Отображение для мобилок */
	if (isMobile) {
		return <ChatMobileContainer>{children}</ChatMobileContainer>;
	}

	/**Отображение для десктопов */
	return (
		<DraggableArea>
			<Resizable
				minHeight={themeProps.minResizableHeight}
				maxHeight={themeProps.maxResizableHeight}
				minWidth={themeProps.minResizableWidth}
				maxWidth={themeProps.maxResizableWidth}
				onResize={onResize}
				size={size}
				className={className}
			>
				{children}
			</Resizable>
			{welcomeDataStore.data?.showcase.showHideButton && (
				<CloseChatDesktopButton onClick={(): void => uiStateStore.setOpened(false)} />
			)}
		</DraggableArea>
	);
};

const ChatContainer = styled(observer(Component))(
	({
		theme: {
			allSettings: {
				containers: { chatContainer },
			},
		},
	}: {
		theme: Theme;
	}) => css`
		div& {
			background: ${chatContainer.background};
			border-radius: ${isMobile ? 0 : chatContainer.borderRadius};
			box-shadow: ${chatContainer.boxShadow};
			display: flex;
			flex-flow: column;
			position: relative;
			transition: height 0.15s linear;
		}
	`
);

export { ChatContainer };
